// Offene Posten von Kunden (Forderungen) und Lieferanten (Verbindlichkeiten), Kontoauszug je Partner, Verrechnen und
// Ausbuchen. Die Beträge führt der Zahlungskern; die Mahnstufe ergibt sich aus den bestätigten Mahnläufen.
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { listInput, listOutput } from '@shared/list'
import { z } from 'zod'
import { AGING_BUCKETS, SIDES, zAging, zAmountCents, zDay, zPartnerRef, zReason } from './common'

const READ = 'finance.openItem.read'
const RECORD = 'finance.payment.record'

export const OPEN_ITEM_STATES = ['open', 'overdue', 'settled', 'all'] as const
export const OPEN_ITEM_SORT_KEYS = ['dueDate', 'openCents', 'partnerName', 'number'] as const

export const zOpenItemRow = z.object({
  id: zId,
  side: z.enum(SIDES),
  partner: zPartnerRef,
  documentId: zId.nullable(),
  documentType: z.string().nullable(),
  /** „Rechnung“, „Rechnungskorrektur“, „Eingangsrechnung“, „Mahngebühr“, „Übernommener Posten“ */
  typeTitle: z.string(),
  number: z.string().nullable(),
  /** Rechnungsnummer des Lieferanten bzw. Bestellnummer des Kunden */
  externalRef: z.string().nullable(),
  documentDate: z.string().nullable(),
  dueDate: z.string().nullable(),
  /** Tage über Fälligkeit zum Stichtag (0 = nicht fällig) */
  daysOverdue: z.int(),
  bucket: z.enum(AGING_BUCKETS),
  /** Rechnung +, Rechnungskorrektur − */
  amountCents: z.int(),
  openCents: z.int(),
  /** open | partial | settled */
  status: z.string(),
  discountUntil: z.string().nullable(),
  discountBp: z.int(),
  /** Skonto, das heute noch abgezogen werden darf – 0 nach Ablauf der Frist */
  discountCents: z.int(),
  dunningLevel: z.int(),
  dunningBlocked: z.boolean(),
  dunningBlockReason: z.string().nullable(),
  lastDunnedAt: z.string().nullable(),
  locationCode: z.string().nullable(),
})
export type OpenItemRow = z.output<typeof zOpenItemRow>

export const openItemList = defineContract({
  name: 'finance.openItem.list',
  title: 'Offene Posten anzeigen',
  description: 'Offene Posten von Kunden oder Lieferanten mit Fälligkeit, Altersstruktur, Mahnstufe und Skontofrist.',
  llm: {
    description:
      'Listet offene Posten: `side: "receivable"` (Standard) sind Forderungen an Kunden, `"payable"` Verbindlichkeiten ' +
      'gegenüber Lieferanten. Je Posten Beleg, Fälligkeit, Tage überfällig, ursprünglicher und offener Betrag in Cent, ' +
      'Skontofrist, Mahnstufe und Mahnsperre. `q` sucht in Kundenname, Kundennummer und Belegnummer – „Welche Rechnungen ' +
      'von Bella Isar sind offen?“ ist `{ q: "Bella Isar" }`. `state`: open (Standard), overdue, settled, all. Summen ' +
      'und Altersstruktur: finance.openItem.summary. Alle Buchungen eines Kunden: finance.openItem.account.',
    keywords: ['offene Posten', 'offene Rechnungen', 'überfällig', 'Forderungen', 'Verbindlichkeiten', 'OP-Liste', 'Außenstände', 'wer schuldet uns'],
  },
  input: listInput({
    sortBy: OPEN_ITEM_SORT_KEYS,
    defaultSort: { by: 'dueDate', dir: 'asc' },
    filters: {
      side: z.enum(SIDES).default('receivable'),
      state: z.enum(OPEN_ITEM_STATES).default('open'),
      partnerId: zId.optional(),
      locationId: zId.optional(),
      /** genau diese Mahnstufe (0 = noch nicht gemahnt) */
      dunningLevel: z.int().min(0).max(9).optional(),
      dueFrom: zDay.optional(),
      dueTo: zDay.optional(),
    },
  }),
  output: listOutput(zOpenItemRow),
  permission: READ,
  risk: 'read',
  scope: 'any',
})

export const openItemSummary = defineContract({
  name: 'finance.openItem.summary',
  title: 'Summen der offenen Posten anzeigen',
  description: 'Offene und überfällige Beträge je Seite, Altersstruktur, nicht zugeordnete Zahlungen.',
  llm: {
    description:
      'Liefert die Summen der offenen Posten für Kunden (receivable) und Lieferanten (payable): offen, davon überfällig, ' +
      'Anzahl, Altersstruktur (nicht fällig, 1–30, 31–60, 61–90, über 90 Tage) und die Summe nicht zugeordneter ' +
      'Zahlungen. Mit `partnerId` nur für einen Partner. Beträge in Cent. Die einzelnen Posten: finance.openItem.list.',
    keywords: ['Außenstände gesamt', 'Altersstruktur', 'überfällig gesamt', 'Forderungsbestand', 'Verbindlichkeiten gesamt'],
  },
  input: z.strictObject({ partnerId: zId.optional() }),
  output: z.object({
    asOf: z.string(),
    receivable: z.object({ openCents: z.int(), overdueCents: z.int(), count: z.int(), overdueCount: z.int(), creditCents: z.int(), aging: zAging }),
    payable: z.object({ openCents: z.int(), overdueCents: z.int(), count: z.int(), overdueCount: z.int(), creditCents: z.int(), aging: zAging }),
    /** Zahlungen ohne Zuordnung: + Guthaben von Kunden bei uns, − unsere Überzahlung an Lieferanten */
    unallocatedCents: z.int(),
    unallocatedCount: z.int(),
  }),
  permission: READ,
  risk: 'read',
  scope: 'any',
})

export const STATEMENT_KINDS = ['opening', 'invoice', 'credit', 'fee', 'legacy', 'payment', 'payment_reversal', 'discount', 'write_off', 'offset'] as const

export const openItemAccount = defineContract({
  name: 'finance.openItem.account',
  title: 'Kontoauszug eines Partners anzeigen',
  description: 'Jede Buchung eines Kunden oder Lieferanten mit ihrem Beleg und laufendem Saldo – druckbar.',
  llm: {
    description:
      'Kontoauszug EINES Partners: jede Rechnung, Rechnungskorrektur, Zahlung, jedes Skonto, jede Verrechnung und ' +
      'Ausbuchung mit Datum, Beleg und laufendem Saldo in Cent. `side: "receivable"` (Standard) zeigt das Kundenkonto, ' +
      '`"payable"` das Lieferantenkonto. Der Endsaldo entspricht den offenen Posten abzüglich nicht zugeordneter ' +
      'Zahlungen. Verwenden für „Was ist bei Kunde X gelaufen?“. Nur die offenen Posten: finance.openItem.list.',
    keywords: ['Kontoauszug', 'Kundenkonto', 'Debitorenkonto', 'Lieferantenkonto', 'Saldo', 'Buchungen des Kunden'],
  },
  input: z.strictObject({ partnerId: zId, side: z.enum(SIDES).default('receivable'), from: zDay.optional(), to: zDay.optional() }),
  output: z.object({
    partner: zPartnerRef.extend({ address: z.array(z.string()), accountNo: z.string().nullable() }),
    side: z.enum(SIDES),
    from: z.string().nullable(),
    to: z.string(),
    openingCents: z.int(),
    rows: z.array(
      z.object({
        key: z.string(),
        kind: z.enum(STATEMENT_KINDS),
        date: z.string(),
        label: z.string(),
        documentId: zId.nullable(),
        documentType: z.string().nullable(),
        number: z.string().nullable(),
        paymentId: zId.nullable(),
        /** erhöht den Saldo (Rechnung) */
        debitCents: z.int(),
        /** mindert den Saldo (Zahlung, Skonto, Rechnungskorrektur) */
        creditCents: z.int(),
        balanceCents: z.int(),
      }),
    ),
    closingCents: z.int(),
    /** Summe der offenen Posten dieser Seite zum heutigen Tag */
    openItemsCents: z.int(),
    overdueCents: z.int(),
    /** nicht zugeordnete Zahlungen (Guthaben) – im Endsaldo bereits abgezogen */
    unallocatedCents: z.int(),
    aging: zAging,
    /** Kopf des Betriebs für den Druck */
    sender: z.object({ name: z.string(), lines: z.array(z.string()), contact: z.array(z.string()), bank: z.array(z.string()) }),
    demo: z.boolean(),
  }),
  permission: READ,
  risk: 'read',
  scope: 'any',
})

export const openItemOffset = defineContract({
  name: 'finance.openItem.offset',
  title: 'Guthaben mit Rechnung verrechnen',
  description: 'Verrechnet eine Rechnungskorrektur oder Gutschrift mit einer offenen Rechnung desselben Partners – ohne Zahlung.',
  llm: {
    description:
      'Verrechnet zwei offene Posten DESSELBEN Partners ohne Zahlung: ein Guthaben (Rechnungskorrektur, ' +
      'Gutschrift – negativer Posten) gegen eine Rechnung. Beide Posten sinken um denselben Betrag; ohne ' +
      '`amountCents` so viel wie möglich. Nicht für Geldeingänge (finance.payment.record) und nicht für überzahlte ' +
      'Beträge einer Zahlung (finance.payment.allocate).',
    keywords: ['verrechnen', 'Gutschrift verrechnen', 'Guthaben anrechnen', 'aufrechnen'],
  },
  input: z.strictObject({ creditItemId: zId, invoiceItemId: zId, amountCents: zAmountCents.optional() }),
  output: z.object({ amountCents: z.int(), items: z.array(z.object({ id: zId, number: z.string().nullable(), openCents: z.int() })) }),
  permission: RECORD,
  risk: 'financial',
  scope: 'record',
})

export const openItemWriteOff = defineContract({
  name: 'finance.openItem.writeOff',
  title: 'Restbetrag ausbuchen',
  description: 'Bucht den offenen Rest eines Postens aus – mit Begründung (Kleindifferenz, uneinbringlich).',
  llm: {
    description:
      'Bucht den offenen Rest eines Postens (oder einen Teil davon, `amountCents` in Cent) aus und schließt ihn damit. ' +
      'Braucht eine Begründung. Verwenden für Kleindifferenzen und uneinbringliche Reste – nicht für Skonto ' +
      '(finance.payment.record) und nicht statt einer Rechnungskorrektur, wenn die Rechnung falsch war.',
    keywords: ['ausbuchen', 'Kleindifferenz', 'Restbetrag', 'uneinbringlich', 'abschreiben'],
  },
  input: z.strictObject({ openItemId: zId, amountCents: zAmountCents.optional(), reason: zReason }),
  output: z.object({ openItemId: zId, writtenOffCents: z.int(), openCents: z.int() }),
  permission: RECORD,
  risk: 'financial',
  scope: 'record',
})
